/**
 * What the emoji over characters and their copies tell the game report (P2.19-T7 and P2.19-T16, ADR-114 decisions 5.2
 * to 5.8 and the amendment of 2026-10-09), the switch a
 * measurement turns them off with, the viewer's Show other players' emotes choice, and the queue the page feeds them
 * through: the page shows every emoji the server's `emojiShown` names, turned into its icon key (`art.ts`), and the
 * surface takes the waiting emoji on its next frame. The queue lives here, beside the counts, because the page receives
 * emoji whether or not the scene is running, and holds at most `MAX_WAITING_EMOJI`, the oldest dropped first, in
 * arrays made once, so queueing allocates nothing.
 */

/**
 * How an emoji is drawn: waiting to pop (held but not yet shown), popped out as its copies, or, under reduced motion,
 * still.
 */
export type EmojiPhase = "waiting" | "popped" | "still";

/**
 * One emoji held, as the report lists it: its character's handle, its icon key, whether its still sprite is drawn, its
 * top left in logical pixels and its side in device pixels, device pixels an art pixel, the UI scale it was placed at
 * and its strength when last drawn, its depth, whether its sprite sits in the UI layer, which the UI camera draws over
 * the whole world, its phase, and how many of its copies were drawn on the last frame.
 */
export interface EmojiReading {
  readonly handle: number;
  readonly key: string;
  readonly shown: boolean;
  readonly left: number;
  readonly top: number;
  readonly size: number;
  readonly k: number;
  readonly scale: number;
  readonly alpha: number;
  readonly depth: number;
  readonly inUiLayer: boolean;
  readonly phase: EmojiPhase;
  readonly copies: number;
}

/**
 * One copy drawn on the last frame, as the report lists it: its emoji's character's handle, its top left in logical
 * pixels, its side in device pixels, device pixels an art pixel, the UI scale, its strength and its depth.
 */
export interface EmojiCopyReading {
  readonly handle: number;
  readonly left: number;
  readonly top: number;
  readonly size: number;
  readonly k: number;
  readonly scale: number;
  readonly alpha: number;
  readonly depth: number;
}

/**
 * The emoji's report, switches and queue. `enabled` false hides every emoji, ends it and drops those taken while it is
 * off; `others` false draws no other character's emoji, the viewer's own still drawn (decision 5.8), and is read each
 * frame; `active`, `created` and `shown` are the pool's emoji held, sprites made so far and emoji drawn, after the last
 * frame; `copies` and `copySprites` are the copies drawn on the last frame and the copy sprites made so far; `received`
 * counts every emoji shown to the page; `list` reads every emoji held and `copyList` every copy drawn; `show` queues an
 * emoji and `take` hands the waiting emoji to a visitor in the order shown, emptying the queue.
 */
export interface EmojiReport {
  enabled: boolean;
  others: boolean;
  active: number;
  created: number;
  shown: number;
  copies: number;
  copySprites: number;
  received: number;
  list: () => readonly EmojiReading[];
  copyList: () => readonly EmojiCopyReading[];
  /**
   * Queues an emoji over a character for the surface's next frame.
   *
   * @param handle The character's handle.
   * @param key The icon key of its art.
   */
  show(handle: number, key: string): void;
  /**
   * Hands every waiting emoji to a visitor, oldest first, and empties the queue.
   *
   * @param visit Told each emoji's handle and key.
   */
  take(visit: (handle: number, key: string) => void): void;
}

/**
 * The most emoji the queue holds while nothing takes them: twice the pool, several frames of a crowd at the rate the
 * map server hands one listener (`EMOJI_PER_LISTENER_SECOND`).
 */
export const MAX_WAITING_EMOJI = 64;

/**
 * A report with nothing drawn or shown yet, other players' emoji on.
 *
 * @returns The report, its list empty until the surface is created.
 */
export function createEmojiReport(): EmojiReport {
  const handles = new Int32Array(MAX_WAITING_EMOJI);
  const keys: string[] = new Array<string>(MAX_WAITING_EMOJI).fill("");
  let head = 0;
  let size = 0;
  const report: EmojiReport = {
    enabled: true,
    others: true,
    active: 0,
    created: 0,
    shown: 0,
    copies: 0,
    copySprites: 0,
    received: 0,
    /**
     * Lists no emoji until the surface is created and replaces this.
     *
     * @returns No readings.
     */
    list: () => [],
    /**
     * Lists no copies until the surface is created and replaces this.
     *
     * @returns No readings.
     */
    copyList: () => [],
    /**
     * Queues an emoji, dropping the oldest waiting one when the queue is full.
     *
     * @param handle The character's handle.
     * @param key The icon key.
     */
    show(handle, key) {
      report.received += 1;
      if (size === MAX_WAITING_EMOJI) {
        head = (head + 1) % MAX_WAITING_EMOJI;
        size -= 1;
      }
      const slot = (head + size) % MAX_WAITING_EMOJI;
      handles[slot] = handle;
      keys[slot] = key;
      size += 1;
    },
    /**
     * Hands every waiting emoji to a visitor, oldest first, freeing each slot before the visit.
     *
     * @param visit Told each emoji's handle and key.
     */
    take(visit) {
      while (size > 0) {
        const handle = handles[head] as number;
        const key = keys[head] as string;
        head = (head + 1) % MAX_WAITING_EMOJI;
        size -= 1;
        visit(handle, key);
      }
    },
  };
  return report;
}
